import React, { useState, useMemo } from 'react';
import { Search, RefreshCw, Users, Clock, Coffee, LogOut, UserMinus, ChevronRight, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Team Status Grid - Professional real-time team member status display
 */
const TeamStatusGrid = ({ teamMembers, loading, onRefresh, onMemberClick }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('CHECKIN');

  const handleMemberRowClick = (member) => {
    // First call the parent callback if provided
    if (onMemberClick) {
      onMemberClick(member);
    }
    // Then navigate to member details page, passing member data via state
    navigate('/home/member-details', { state: { member } });
  };

  const statusConfig = {
    CHECKIN: {
      label: 'Working',
      shortLabel: 'Active',
      icon: Clock,
      color: '#059669',
      lightColor: '#10b981',
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      badge: 'bg-emerald-1000',
      ring: 'ring-emerald-1000',
      description: 'Currently checked in'
    },
    BREAK: {
      label: 'On Break',
      shortLabel: 'Break',
      icon: Coffee,
      color: '#d97706',
      lightColor: '#f59e0b',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      badge: 'bg-amber-1000',
      ring: 'ring-amber-1000',
      description: 'Taking a break'
    },
    CHECKOUT: {
      label: 'Checked Out',
      shortLabel: 'Left',
      icon: LogOut,
      color: '#6b7280',
      lightColor: '#9ca3af',
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      text: 'text-gray-700',
      badge: 'bg-gray-1000',
      ring: 'ring-gray-1000',
      description: 'Left for the day'
    },
    NOT_CHECKED_IN: {
      label: 'Not Checked In',
      shortLabel: 'Absent',
      icon: UserMinus,
      color: '#dc2626',
      lightColor: '#ef4444',
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      badge: 'bg-red-1000',
      ring: 'ring-red-1000',
      description: 'Not checked in today'
    },
  };

  const formatHours = (hours) => {
    if (!hours || hours === 0) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '--:--';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  // Group members by status
  const groupedMembers = useMemo(() => {
    const groups = { CHECKIN: [], BREAK: [], CHECKOUT: [], NOT_CHECKED_IN: [] };
    teamMembers?.forEach((member) => {
      const status = member.current_status?.toUpperCase();
      if (status === 'CHECKIN') {
        groups.CHECKIN.push(member);
      } else if (status === 'BREAK') {
        groups.BREAK.push(member);
      } else if (status === 'CHECKOUT') {
        groups.CHECKOUT.push(member);
      } else {
        // NULL, empty, or any unexpected status goes to NOT_CHECKED_IN
        groups.NOT_CHECKED_IN.push(member);
      }
    });
    return groups;
  }, [teamMembers]);

  // Filter by search
  const filteredMembers = useMemo(() => {
    const members = groupedMembers[selectedStatus] || [];
    if (!searchTerm) return members;
    return members.filter((m) =>
      m.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.employee_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [groupedMembers, selectedStatus, searchTerm]);

  // Calculate totals
  const totals = useMemo(() => ({
    working: groupedMembers.CHECKIN.length,
    break: groupedMembers.BREAK.length,
    left: groupedMembers.CHECKOUT.length,
    notCheckedIn: groupedMembers.NOT_CHECKED_IN.length,
    total: teamMembers?.length || 0
  }), [groupedMembers, teamMembers]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-gray-50">
          <div className="animate-pulse flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="animate-pulse grid grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-gray-100 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render a status tab card
  const renderStatusTab = (status) => {
    const config = statusConfig[status];
    const count = groupedMembers[status]?.length || 0;
    const isActive = selectedStatus === status;
    const Icon = config.icon;

    // Use inline styles for colors to avoid Tailwind purge issues
    const bgColors = { CHECKIN: '#ecfdf5', BREAK: '#fffbeb', CHECKOUT: '#f3f4f6', NOT_CHECKED_IN: '#fef2f2' };
    const borderColors = { CHECKIN: '#a7f3d0', BREAK: '#fde68a', CHECKOUT: '#d1d5db', NOT_CHECKED_IN: '#fecaca' };
    const textColors = { CHECKIN: '#047857', BREAK: '#b45309', CHECKOUT: '#374151', NOT_CHECKED_IN: '#dc2626' };

    const activeStyles = isActive ? {
      backgroundColor: bgColors[status],
      borderColor: borderColors[status],
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    } : {};

    const textColor = isActive ? textColors[status] : '#374151';

    const iconBgColor = isActive ? config.color : '#e5e7eb';

    // Ring color for selected state
    const ringStyle = isActive ? {
      outline: `3px solid ${config.color}`,
      outlineOffset: '2px'
    } : {};

    return (
      <button
        key={status}
        onClick={() => setSelectedStatus(status)}
        className={`flex-1 relative p-3 rounded-xl transition-all duration-200 border-2 ${
          !isActive ? 'bg-gray-50 border-transparent hover:bg-gray-100' : ''
        }`}
        style={{
          ...activeStyles,
          ...ringStyle
        }}
      >
        <div className="flex flex-col items-center gap-1">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center transition-all"
            style={{ backgroundColor: iconBgColor }}
          >
            <Icon size={20} className="text-white" style={{ color: isActive ? 'white' : '#4b5563' }} />
          </div>
          <span
            className="text-2xl font-bold"
            style={{ color: textColor }}
          >
            {count}
          </span>
          <span
            className="text-xs font-medium"
            style={{ color: isActive ? textColor : '#6b7280' }}
          >
            {config.label}
          </span>
        </div>
        {status === 'CHECKIN' && count > 0 && (
          <span className="absolute top-2 right-2 flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-1000"></span>
          </span>
        )}
      </button>
    );
  };

  const MemberCard = ({ member }) => {
    const config = statusConfig[selectedStatus];
    const sessionTime = selectedStatus === 'CHECKIN' ? formatHours(member.current_session_hours) : formatTime(member.last_activity_time);

    return (
      <div
        onClick={() => handleMemberRowClick(member)}
        className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 hover:shadow-sm cursor-pointer transition-all group"
      >
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          {member.user_image ? (
            <img
              src={`data:image/jpeg;base64,${member.user_image}`}
              alt={member.full_name}
              className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow"
            />
          ) : (
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold ring-2 ring-white shadow"
              style={{ backgroundColor: config.color }}
            >
              {member.full_name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
          <span
            className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${config.badge}`}
          />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate group-hover:text-gray-900">
            {member.full_name || 'Unknown'}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-gray-1000">
              {member.employee_id || member.role_name || 'Employee'}
            </span>
            {member.location_name && (
              <>
                <span className="text-gray-300">•</span>
                <span className="text-xs text-gray-1000 truncate">{member.location_name}</span>
              </>
            )}
          </div>
        </div>

        {/* Time/Duration */}
        <div className="text-right flex-shrink-0">
          <p className={`text-sm font-bold ${config.text}`}>
            {sessionTime}
          </p>
          <p className="text-xs text-gray-400">
            {selectedStatus === 'CHECKIN' ? 'active' : 'last seen'}
          </p>
        </div>

        <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-400 flex-shrink-0" />
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-1000 to-blue-600 flex items-center justify-center shadow-sm">
              <Users size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-800">Team Status</h3>
              <div className="flex items-center gap-2 text-xs text-gray-1000">
                <Activity size={12} className="text-emerald-1000" />
                <span>{totals.working} active</span>
                <span className="text-gray-300">•</span>
                <span>{totals.total} total</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search team..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-1000 focus:border-transparent w-36 bg-white"
              />
            </div>

            <button
              onClick={onRefresh}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors bg-white"
              title="Refresh"
            >
              <RefreshCw size={14} className="text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="p-4 bg-gray-50/50">
        <div className="flex gap-3">
          {renderStatusTab('CHECKIN')}
          {renderStatusTab('BREAK')}
          {renderStatusTab('CHECKOUT')}
          {renderStatusTab('NOT_CHECKED_IN')}
        </div>
      </div>

      {/* Member List */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-700">
            {statusConfig[selectedStatus].label}
            <span className="ml-2 text-gray-400 font-normal">
              ({filteredMembers.length})
            </span>
          </h4>
          <span className="text-xs text-gray-400">
            {statusConfig[selectedStatus].description}
          </span>
        </div>

        {filteredMembers.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1 custom-scrollbar">
            {filteredMembers.map((member) => (
              <MemberCard key={member.user_id} member={member} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <Users size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm text-gray-1000">
              {searchTerm ? 'No matching members' : `No team members ${statusConfig[selectedStatus].description.toLowerCase()}`}
            </p>
          </div>
        )}
      </div>

      {/* Footer Summary */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-1000"></span>
              <span className="text-gray-600">{totals.working} working</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-1000"></span>
              <span className="text-gray-600">{totals.break} on break</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-gray-1000"></span>
              <span className="text-gray-600">{totals.left} left</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-1000"></span>
              <span className="text-gray-600">{totals.notCheckedIn} absent</span>
            </span>
          </div>
          <button
            onClick={() => navigate('/home/members', { state: { statusFilter: selectedStatus } })}
            className="text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
            title="View all members with this status"
          >
            View All
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamStatusGrid;
